import { Action, Reducer } from 'redux';
import { AppThunkAction } from '..';
import { ArtifactImportPayload } from '../../Interfaces/ProductsManagement/ArtifactImportPayload';
import { ArtifactPackageHashMap } from '../../Interfaces/ProductsManagement/ArtifactPackageHashMap';
import { NodeDeploymentInfo } from '../../Interfaces/ProductsManagement/NodeDeploymentInfo';
import { ProductPackageHealthInfo } from '../../Interfaces/ProductsManagement/ProductPackageHealthInfo';
import { ProductPackagesInfo } from '../../Interfaces/ProductsManagement/ProductPackagesInfo';
import { getRequestOptions, postRequestOptions } from '../../utils/utils';

export enum ProductsActionTypes {
  VALIDATE_ARTIFACT_VERSIONS = 'VALIDATE_ARTIFACT_VERSIONS',
  RECEIVED_ARTIFACT_VERSIONS = 'RECEIVED_ARTIFACT_VERSIONS',
  REQUEST_REGISTER_ARTIFACTS_IMPORT = 'REQUEST_REGISTER_ARTIFACTS_IMPORT',
  RECEIVED_REGISTER_ARTIFACTS_IMPORT = 'RECEIVED_REGISTER_ARTIFACTS_IMPORT',
  REQUEST_PRODUCT_PACKAGES_VERSIONS = 'REQUEST_PRODUCT_PACKAGES_VERSIONS',
  RECEIVED_PRODUCT_PACKAGES_VERSIONS = 'RECEIVED_PRODUCT_PACKAGES_VERSIONS',
  REQUEST_ACTIVATE_PACKAGE_VERSION = 'REQUEST_ACTIVATE_PACKAGE_VERSION',
  RECEIVED_ACTIVATE_PACKAGE_VERSION = 'RECEIVED_ACTIVATE_PACKAGE_VERSION',
  FAILED_ACTIVATE_PACKAGE_VERSION = 'FAILED_ACTIVATE_PACKAGE_VERSION',
  RESET_ACTIVATION_FLAGS = 'RESET_ACTIVATION_FLAGS',
  REQUEST_NODE_DEPLOYMENT_INFO = 'REQUEST_NODE_DEPLOYMENT_INFO',
  RECEIVED_NODE_DEPLOYMENT_INFO = 'RECEIVED_NODE_DEPLOYMENT_INFO',
  REQUEST_PRODUCT_PACKAGES_HEALTH_INFO = 'REQUEST_PRODUCT_PACKAGES_HEALTH_INFO',
  RECEIVED_PRODUCT_PACKAGES_HEALTH_INFO = 'RECEIVED_PRODUCT_PACKAGES_HEALTH_INFO',
  RECEIVED_ARTIFACTS_HTTP_RESPONSE_ERROR_CODE = 'RECEIVED_ARTIFFACTS_HTTP_RESPONSE_ERROR_CODE',
  RESET_ARTIFACTS_HTTP_RESPONSE_ERROR_CODE = 'RESET_ARTIFACTS_HTTP_RESPONSE_ERROR_CODE',
}

export interface ProductsState {
  artifactNameVersionMap: ArtifactPackageHashMap[];
  isFetching: boolean;
  productPackagesVersions: ProductPackagesInfo[];
  nodeDeploymentInfo: NodeDeploymentInfo[];
  productPackagesHealthInfo: ProductPackageHealthInfo[];
  activatePackageVersionSuccess: boolean;
  activatePackageVersionFailed: boolean;
}

interface ValidateArtifactVersionsAction {
  type: ProductsActionTypes.VALIDATE_ARTIFACT_VERSIONS;
}
interface ReceivedArtifactVersionsAction {
  type: ProductsActionTypes.RECEIVED_ARTIFACT_VERSIONS;
  values: ArtifactPackageHashMap[];
}
interface RequestRegisterArtifactsImportAction {
  type: ProductsActionTypes.REQUEST_REGISTER_ARTIFACTS_IMPORT;
}
interface ReceivedRegisterArtifactsImportAction {
  type: ProductsActionTypes.RECEIVED_REGISTER_ARTIFACTS_IMPORT;
}
interface RequestProductPackagesVersionsAction {
  type: ProductsActionTypes.REQUEST_PRODUCT_PACKAGES_VERSIONS;
}
interface ReceivedProductPackagesVersionsAction {
  type: ProductsActionTypes.RECEIVED_PRODUCT_PACKAGES_VERSIONS;
  values: ProductPackagesInfo[];
}
interface RequestActivateProductVersionAction {
  type: ProductsActionTypes.REQUEST_ACTIVATE_PACKAGE_VERSION;
}
interface ReceivedActivateProductVersionAction {
  type: ProductsActionTypes.RECEIVED_ACTIVATE_PACKAGE_VERSION;
}
interface FailedActivateProductVersionAction {
  type: ProductsActionTypes.FAILED_ACTIVATE_PACKAGE_VERSION;
}
interface ResetActivationFlagsAction {
  type: ProductsActionTypes.RESET_ACTIVATION_FLAGS;
}
interface RequestNodeDeploymentInfo {
  type: ProductsActionTypes.REQUEST_NODE_DEPLOYMENT_INFO;
}
interface ReceivedNodeDeploymentInfo {
  type: ProductsActionTypes.RECEIVED_NODE_DEPLOYMENT_INFO;
  values: NodeDeploymentInfo[];
}
interface RequestProductPackagesHealthInfo {
  type: ProductsActionTypes.REQUEST_PRODUCT_PACKAGES_HEALTH_INFO;
}
interface ReceivedProductPackagesHealthInfo {
  type: ProductsActionTypes.RECEIVED_PRODUCT_PACKAGES_HEALTH_INFO;
  values: ProductPackageHealthInfo[];
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ValidateArtifactVersionsAction
  | ReceivedArtifactVersionsAction
  | RequestRegisterArtifactsImportAction
  | ReceivedRegisterArtifactsImportAction
  | RequestProductPackagesVersionsAction
  | ReceivedProductPackagesVersionsAction
  | RequestActivateProductVersionAction
  | ReceivedActivateProductVersionAction
  | FailedActivateProductVersionAction
  | ResetActivationFlagsAction
  | RequestNodeDeploymentInfo
  | ReceivedNodeDeploymentInfo
  | RequestProductPackagesHealthInfo
  | ReceivedProductPackagesHealthInfo;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).
const isResponseOk = (response: Response, dispatch: any): boolean => {
  if (response.ok) return true;

  console.error(`Products call failed, status: ${response.status}`);
  dispatch({
    type: ProductsActionTypes.RECEIVED_ARTIFACTS_HTTP_RESPONSE_ERROR_CODE,
    statusCode: response.status,
  });
  return false;
};

export const actionCreators = {
  validateArtifactVersions:
    (artifacts: ArtifactPackageHashMap[]): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${ProductsActionTypes.VALIDATE_ARTIFACT_VERSIONS}`,
      );
      dispatch({
        type: ProductsActionTypes.VALIDATE_ARTIFACT_VERSIONS,
      });

      const response = await fetch(
        `Products/ValidatePackageHash`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          artifacts,
        ),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as ArtifactPackageHashMap[];

      console.log(
        `Dispatching ${ProductsActionTypes.RECEIVED_ARTIFACT_VERSIONS}`,
      );

      dispatch({
        type: ProductsActionTypes.RECEIVED_ARTIFACT_VERSIONS,
        values: result,
      });
    },

  registerArtifactsImport:
    (
      sessionId: string,
      artifactImportpayload: ArtifactImportPayload,
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${ProductsActionTypes.REQUEST_REGISTER_ARTIFACTS_IMPORT}`,
      );
      dispatch({
        type: ProductsActionTypes.REQUEST_REGISTER_ARTIFACTS_IMPORT,
      });

      const response = await fetch(
        `Products/RegisterArtifactsImport/${sessionId}`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          artifactImportpayload,
        ),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      console.log(
        `Dispatching ${ProductsActionTypes.RECEIVED_REGISTER_ARTIFACTS_IMPORT}`,
      );

      dispatch({
        type: ProductsActionTypes.RECEIVED_REGISTER_ARTIFACTS_IMPORT,
      });
    },

  getProductPackagesVersions:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${ProductsActionTypes.REQUEST_PRODUCT_PACKAGES_VERSIONS}`,
      );
      dispatch({
        type: ProductsActionTypes.REQUEST_PRODUCT_PACKAGES_VERSIONS,
      });

      const response = await fetch(
        `Products/GetProductPackageVersions`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as ProductPackagesInfo[];

      console.log(
        `Dispatching ${ProductsActionTypes.RECEIVED_PRODUCT_PACKAGES_VERSIONS}`,
      );

      dispatch({
        type: ProductsActionTypes.RECEIVED_PRODUCT_PACKAGES_VERSIONS,
        values: result,
      });
    },

  activatePackageVersion:
    (productPackageId: number): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${ProductsActionTypes.REQUEST_ACTIVATE_PACKAGE_VERSION}`,
      );
      dispatch({
        type: ProductsActionTypes.REQUEST_ACTIVATE_PACKAGE_VERSION,
      });

      const response = await fetch(
        `Products/ActivatePackageVersion`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          productPackageId,
        ),
      ).catch(error => {
        console.error(error);
        dispatch({
          type: ProductsActionTypes.FAILED_ACTIVATE_PACKAGE_VERSION,
        });
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) {
        dispatch({
          type: ProductsActionTypes.FAILED_ACTIVATE_PACKAGE_VERSION,
        });
        return;
      }

      console.log(
        `Dispatching ${ProductsActionTypes.RECEIVED_ACTIVATE_PACKAGE_VERSION}`,
      );

      dispatch({
        type: ProductsActionTypes.RECEIVED_ACTIVATE_PACKAGE_VERSION,
      });

      await (dispatch as any)(actionCreators.getProductPackagesVersions());
    },

  resetActivationFlags:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(`Dispatching ${ProductsActionTypes.RESET_ACTIVATION_FLAGS}`);

      dispatch({
        type: ProductsActionTypes.RESET_ACTIVATION_FLAGS,
      });
    },

  getNodeDeploymentInfo:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${ProductsActionTypes.REQUEST_NODE_DEPLOYMENT_INFO}`,
      );
      dispatch({
        type: ProductsActionTypes.REQUEST_NODE_DEPLOYMENT_INFO,
      });

      const response = await fetch(
        `Products/GetNodeDeploymentInfo`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          appState.userSettingsReducer.productsHealthFilter,
        ),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as NodeDeploymentInfo[];

      console.log(
        `Dispatching ${ProductsActionTypes.RECEIVED_NODE_DEPLOYMENT_INFO}`,
      );

      dispatch({
        type: ProductsActionTypes.RECEIVED_NODE_DEPLOYMENT_INFO,
        values: result,
      });
    },

  getProductPackagesHealthInfo:
    (nodeId: number): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${ProductsActionTypes.REQUEST_PRODUCT_PACKAGES_HEALTH_INFO}`,
      );
      dispatch({
        type: ProductsActionTypes.REQUEST_PRODUCT_PACKAGES_HEALTH_INFO,
      });

      const response = await fetch(
        `Products/GetProductPackagesHealthInfo/${nodeId}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as ProductPackageHealthInfo[];

      console.log(
        `Dispatching ${ProductsActionTypes.RECEIVED_PRODUCT_PACKAGES_HEALTH_INFO}`,
      );

      dispatch({
        type: ProductsActionTypes.RECEIVED_PRODUCT_PACKAGES_HEALTH_INFO,
        values: result,
      });
    },
};

export const unloadedState: ProductsState = {
  artifactNameVersionMap: [],
  isFetching: false,
  productPackagesVersions: [],
  nodeDeploymentInfo: [],
  productPackagesHealthInfo: [],
  activatePackageVersionSuccess: false,
  activatePackageVersionFailed: false,
};

export const reducer: Reducer<ProductsState> = (
  state: ProductsState | undefined,
  incomingAction: Action,
): ProductsState => {
  if (state === undefined) {
    return unloadedState;
  }

  const action = incomingAction as KnownAction;
  switch (action.type) {
    case ProductsActionTypes.RECEIVED_ARTIFACT_VERSIONS:
      return {
        ...state,
        artifactNameVersionMap: action.values,
      };
    case ProductsActionTypes.REQUEST_PRODUCT_PACKAGES_VERSIONS:
      return {
        ...state,
        isFetching: true,
      };
    case ProductsActionTypes.RECEIVED_PRODUCT_PACKAGES_VERSIONS:
      return {
        ...state,
        productPackagesVersions: action.values,
        isFetching: false,
      };
    case ProductsActionTypes.REQUEST_ACTIVATE_PACKAGE_VERSION:
      return {
        ...state,
        activatePackageVersionSuccess: false,
        activatePackageVersionFailed: false,
      };
    case ProductsActionTypes.RECEIVED_ACTIVATE_PACKAGE_VERSION:
      return {
        ...state,
        activatePackageVersionSuccess: true,
        activatePackageVersionFailed: false,
      };
    case ProductsActionTypes.FAILED_ACTIVATE_PACKAGE_VERSION:
      return {
        ...state,
        activatePackageVersionSuccess: false,
        activatePackageVersionFailed: true,
      };
    case ProductsActionTypes.RESET_ACTIVATION_FLAGS:
      return {
        ...state,
        activatePackageVersionSuccess: false,
        activatePackageVersionFailed: false,
      };
    case ProductsActionTypes.REQUEST_NODE_DEPLOYMENT_INFO:
      return {
        ...state,
        isFetching: true,
      };
    case ProductsActionTypes.RECEIVED_NODE_DEPLOYMENT_INFO:
      return {
        ...state,
        isFetching: false,
        nodeDeploymentInfo: action.values,
      };
    case ProductsActionTypes.REQUEST_PRODUCT_PACKAGES_HEALTH_INFO:
      return {
        ...state,
        isFetching: true,
      };
    case ProductsActionTypes.RECEIVED_PRODUCT_PACKAGES_HEALTH_INFO:
      return {
        ...state,
        isFetching: false,
        productPackagesHealthInfo: action.values,
      };
    default:
      break;
  }
  return state;
};
